import { useEffect, useMemo, useState } from 'react';
import { useProvider } from '~/Hooks';
import {
  ITranslatedTexts,
  ITranslatedTextsAsArray,
  ITranslatedTextsAsObject,
} from '~/Models/interfaces/translatedText.model';

type ITranslationObjectHandler = {
  translatedTexts: ITranslatedTexts;
  valuesAsArray?: boolean;
  currentLanguage: string;
};

const getTextFromTranslationObject = ({
  translatedTexts,
  valuesAsArray,
  currentLanguage,
}: ITranslationObjectHandler) => {
  if (!translatedTexts || typeof translatedTexts === 'string') {
    return '';
  }
  if (valuesAsArray) {
    return (
      (translatedTexts as ITranslatedTextsAsArray)?.find(
        (text) => text.language === currentLanguage,
      )?.text || ''
    );
  }
  return (translatedTexts as ITranslatedTextsAsObject)[currentLanguage] ?? '';
};

const updateTextInTranslationOject = ({
  translatedTexts,
  valuesAsArray,
  currentLanguage,
  text,
}: {
  text: string;
} & ITranslationObjectHandler) => {
  let translatedTextsNotEmpty = translatedTexts;
  if (typeof translatedTexts === 'string') {
    return {};
  }
  if (valuesAsArray) {
    translatedTextsNotEmpty = translatedTextsNotEmpty || [];
    const textIndex = (translatedTextsNotEmpty as ITranslatedTextsAsArray)?.findIndex(
      (text) => text.language === currentLanguage,
    );
    if (textIndex !== undefined && textIndex !== -1) {
      (translatedTextsNotEmpty as ITranslatedTextsAsArray)[textIndex].text = text;
    } else {
      (translatedTextsNotEmpty as ITranslatedTextsAsArray).push({
        language: currentLanguage,
        text,
      });
    }
  } else {
    translatedTextsNotEmpty = translatedTextsNotEmpty || {};
    (translatedTextsNotEmpty as ITranslatedTextsAsObject)[currentLanguage] = text;
  }
  return translatedTextsNotEmpty;
};

export const useTextEditor = ({
  onChange,
  defaultValues,
  fieldName,
  valuesAsArray,
}: {
  onChange: (field: string, value: any) => void;
  defaultValues: ITranslatedTexts;
  fieldName: string;
  valuesAsArray?: boolean;
}) => {
  const { currentProvider } = useProvider();
  const providerLanguage = currentProvider?.language.primaryLanguage.toUpperCase() || 'DE';
  const [currentLanguage, setCurrentLanguage] = useState(providerLanguage);
  const [currentText, setCurrentText] = useState(() =>
    getTextFromTranslationObject({
      currentLanguage,
      valuesAsArray,
      translatedTexts: defaultValues,
    }),
  );
  const [touchedTexts, setTouchedTexts] = useState<ITranslatedTexts>(defaultValues);

  const defaultText = useMemo(
    () =>
      getTextFromTranslationObject({
        currentLanguage: providerLanguage,
        valuesAsArray,
        translatedTexts: defaultValues,
      }),
    [defaultValues, providerLanguage],
  );

  const changeLanguage = (newLanguage: string) => () => {
    setCurrentLanguage(newLanguage);
  };

  useEffect(() => {
    setCurrentText(defaultText);
  }, [defaultText]);

  useEffect(() => {
    setTouchedTexts(defaultValues);
  }, [defaultValues]);

  useEffect(() => {
    setCurrentText(
      getTextFromTranslationObject({
        currentLanguage,
        valuesAsArray,
        translatedTexts: touchedTexts,
      }),
    );
  }, [currentLanguage, touchedTexts]);

  useEffect(() => {
    const newValue = updateTextInTranslationOject({
      currentLanguage,
      valuesAsArray,
      text: currentText || '',
      translatedTexts: touchedTexts,
    });
    setTouchedTexts(newValue);
    onChange(fieldName, newValue);
  }, [currentText, currentLanguage]);

  return {
    changeLanguage,
    setCurrentText,
    defaultText,
    currentText,
    currentLanguage,
    providerLanguage,
  };
};
